<template>
  <BContainer class="container-pages" v-if="customer">
   <BRow>
    <BCol xl="8">
      <BRow>
      <BCol xl="4" lg="4" class="d-xl-block d-lg-block d-md-none d-sm-none d-none" >
            <div class="p-element-page shadow bg-white rounded" >
             <FilterCategory :categories="categories"></FilterCategory>
           </div>
        </BCol>
      <BCol xl="8" lg="8">
      <RatingCard  
          :customer="customer"
          :summary="summary"
          :permissions="permissions"
          :ratings="ratingsWithPermissions"
          :pagination="pagination"
          :loading="loading"
          :selected-tab="activeTab"
          :selected-rate="customerRate"
          :rate-options="rateOptions"
          :customer-id="customerId"
          @change-tab="handleTabChange"
          @change-rate="handleRateChange"
          @change-page="handlePageChange"
          @refresh-data="refresh"
        />
      </BCol>
   </BRow>
  </BCol>
    <BCol xl="4" lg="12" md="12">
          <div class="a-container mt-3 mb-3" v-if="$screen.showDesktopAds">
               <AdsBottomPage></AdsBottomPage>              
          </div>
      <TopAdsPages
          class="mt-3"
          v-if="topAds"
          :items="topAds"
          :category_name="''"
          :display_img="true"
        />   
     </BCol>
     <BCol  class="d-xl-none d-lg-none d-md-block d-sm-block d-block mt-2" v-if="!$screen.isDesktop">
            <div class="p-element-page shadow bg-white rounded">
             <FilterCategory :categories="categories"></FilterCategory>
           </div>
      </BCol>

  </BRow>

  </BContainer>
  
</template>

<script setup>
import { BContainer, BRow, BCol } from 'bootstrap-vue-next';
import FilterCategory from '~/components/cmsCustomer/advertisement/filter/FilterCategory.vue';
import TopAdsPages from '~/components/widgets/Advertisement/TopAdsPages.vue'
 
const AdsBottomPage = defineAsyncComponent(() =>
  import('~/components/GoogleAdvertisements/AdsBottomPage.vue')
)

 
import RatingCard from '~/components/cms/rating/RatingCard.vue'
import { useCustomerLoginStore } from '~/stores/login/customer'

const useStore = useCustomerLoginStore()
const route = useRoute()
const router = useRouter()
const { $api } = useNuxtApp()

const customerId = computed(() => Number(route.params.customerid))
const customerType = computed(() => String(route.params.customertype || 'prodavac-kupac'))
const customerName = computed(() => String(route.params.customername || ''))
const customerRate = computed(() => String(route.params.customerrate || 'sve-ocene'))

const pageParam = computed(() => {
  const page = Number(route.params.page || 1)
  return Number.isNaN(page) || page < 1 ? 1 : page
})

const filters = reactive({
  page: pageParam.value,
  perPage: 10
})

const tabOptions = {
  all: {
    routeType: 'prodavac-kupac',
    apiType: 'all'
  },
  seller: {
    routeType: 'prodavac',
    apiType: 'seller'
  },
  buyer: {
    routeType: 'kupac',
    apiType: 'buyer'
  }
}

const rateOptions = [
  { value: 'sve-ocene', text: 'Sve ocene' },
  { value: 'pozitivno', text: 'Pozitivne' },
  { value: 'negativno', text: 'Negativne' }
]

const activeTab = computed(() => {
  if (customerType.value === 'prodavac') return 'seller'
  if (customerType.value === 'kupac') return 'buyer'
  return 'all'
})

const tabToType = computed(() => {
  return tabOptions[activeTab.value]?.apiType || 'all'
})

const rateFilter = computed(() => {
  if (customerRate.value === 'pozitivno') return 'positive'
  if (customerRate.value === 'negativno') return 'negative'
  return 'all'
})

const asyncKey = computed(() =>
  `customer-ratings-${customerId.value}-${tabToType.value}-${rateFilter.value}-${filters.page}-${filters.perPage}`
)

const {
  data: ratingsResponse,
  pending: loading,
  error,
  refresh
} = await useAsyncData(
  asyncKey,
  async () => {
    const response = await $api(`/api/ad/customers/${customerId.value}/ratings`, {
      method: 'GET',
      query: {
        type: tabToType.value,
        rate: rateFilter.value,
        per_page: filters.perPage,
        page: filters.page
      }
    })

    return response
  },
  {
    watch: [
      customerId,
      tabToType,
      rateFilter,
      () => filters.page,
      () => filters.perPage
    ] 
  }
)

const ratings = computed(() => {
  return ratingsResponse.value?.data?.ratings ?? []
})

const categories = computed(() => {
  return ratingsResponse.value?.data?.categories ?? []
})

const topAds = computed(() => {
  return ratingsResponse.value?.data?.topAds ?? []
})

const summary = computed(() => {
  return {
    total: ratingsResponse.value?.data?.summary?.total ?? 0,
    positive_count: ratingsResponse.value?.data?.summary?.positive_count ?? 0,
    negative_count: ratingsResponse.value?.data?.summary?.negative_count ?? 0
  }
})

const customer = computed(() => {
  return {
    id: customerId.value,
    customer_name: ratingsResponse.value?.data?.summary?.customer_name ?? '',
    customer_created_at: ratingsResponse.value?.data?.summary?.customer_created_at ?? ''
  }
})

const permissions = computed(() => {
  return {
    can_rate: true//Boolean(ratingsResponse.value?.permissions?.can_rate)
  }
})

const pagination = computed(() => {
  return {
    current_page: ratingsResponse.value?.meta?.current_page ?? 1,
    per_page: ratingsResponse.value?.meta?.per_page ?? filters.perPage,
    total: ratingsResponse.value?.meta?.total ?? 0,
    last_page: ratingsResponse.value?.meta?.last_page ?? 1
  }
})

const authCustomerId = computed(() => {
  return useStore?.customer?.id || null
})

const ratingsWithPermissions = computed(() => {
  return ratings.value.map(item => {
    const currentCustomerId = authCustomerId.value

    return {
      ...item,
      can_reply:
        !!currentCustomerId &&
        Number(currentCustomerId) === Number(item.to_customer_id) &&
        !item.reply_comment,

      can_edit_comment:
        !!currentCustomerId &&
        Number(currentCustomerId) === Number(item.from_customer_id),

      can_edit_reply:
        !!currentCustomerId &&
        Number(currentCustomerId) === Number(item.to_customer_id) &&
        !!item.reply_comment
    }
  })
})

const updateRoute = async ({
  tab = activeTab.value,
  rate = customerRate.value,
  page = filters.page
} = {}) => {
  const selectedTab = tabOptions[tab] || tabOptions.all

  await router.push({
    name: 'oglas-ocene-customername-customerid-customertype-customerrate-page',
    params: {
      customername: customerName.value,
      customerid: customerId.value,
      customertype: selectedTab.routeType,
      customerrate: rate || 'sve-ocene',
      page: page || 1
    }
  })
}

const handleTabChange = async (tab) => {
  if (activeTab.value === tab) return

  filters.page = 1
  await updateRoute({ tab, page: 1 })
}

const handleRateChange = async (rate) => {
  if (customerRate.value === rate) return

  filters.page = 1
  await updateRoute({ rate, page: 1 })
}

const handlePageChange = async (page) => {
  filters.page = page
  await updateRoute({ page })
}

watch(
  () => route.params.page,
  () => {
    filters.page = pageParam.value
  }
)

const pageTitle = computed(() => {
  const name = customer?.value?.customer_name || 'Korisnik'
  return `Ocene korisnika ${name} | Pijace.com`
})

const pageDescription = computed(() => {
  const name = customer?.value?.customer_name || 'korisnik'
  const total = summary.value?.total ?? 0
  const positive = summary.value?.positive_count ?? 0
  const negative = summary.value?.negative_count ?? 0

  return `Pogledajte ocene korisnika ${name} na sajtu Pijace.com. Ukupno ocena: ${total}, pozitivnih: ${positive}, negativnih: ${negative}.`
})

const canonicalUrl = computed(() => {
  return `https://pijace.com/oglas-ocene/${customerName.value}/${customerId.value}/${customerType.value}/${customerRate.value}/${filters.page}`
})

// JSON-LD: Person sa AggregateRating (ocene prodavca/kupca)
const personLd = computed(() => {
  const name = customer.value?.customer_name
  const total = Number(summary.value?.total) || 0
  if (!name || total === 0) return null

  // Pretpostavljamo binarni rating (pozitivno/negativno) → vrednost izmedju 1-5
  // Ako su sve pozitivne → 5, sve negativne → 1, mix → proporcionalno
  const positive = Number(summary.value?.positive_count) || 0
  // Skala 1-5: ako je 100% pozitivno → 5, 0% → 1
  const ratingValue = total > 0 ? (1 + (positive / total) * 4).toFixed(2) : '3'

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${canonicalUrl.value}#person`,
    name,
    url: canonicalUrl.value,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue,
      reviewCount: total,
      bestRating: '5',
      worstRating: '1'
    }
  }
})

// JSON-LD: ItemList sa top 10 recenzija
const reviewsLd = computed(() => {
  const all = ratings.value || []
  if (all.length === 0) return null

  const list = all.slice(0, 10)
  const name = customer.value?.customer_name || 'Korisnik'

  const itemListElement = list.map((r, i) => {
    const reviewerName = r?.from_customer_name || 'Korisnik'
    // Rating: pozitivno → 5, negativno → 1
    const isPositive = String(r?.rate || '').toLowerCase() === 'positive'
      || String(r?.rate || '').toLowerCase() === 'pozitivno'
      || r?.rate === 1 || r?.rate === '1'

    return {
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: reviewerName
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: isPositive ? '5' : '1',
          bestRating: '5',
          worstRating: '1'
        },
        ...(r?.comment ? { reviewBody: String(r.comment).slice(0, 500) } : {}),
        ...(r?.created_at ? { datePublished: r.created_at } : {}),
        itemReviewed: {
          '@type': 'Person',
          name
        }
      }
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Ocene korisnika ${name}`,
    numberOfItems: all.length,
    itemListElement
  }
})

// JSON-LD: BreadcrumbList — Oglasi → korisnik → Ocene
const breadcrumbLd = computed(() => {
  if (!customer.value?.customer_name) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Oglasi',
        item: 'https://pijace.com/oglasi/svi-oglasi/1'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: customer.value.customer_name,
        item: `https://pijace.com/oglas/svi-oglasi/${customerName.value}/${customerId.value}`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Ocene',
        item: canonicalUrl.value
      }
    ]
  }
})

usePijaceSeo({
  title: pageTitle,
  description: pageDescription,
  url: canonicalUrl,
  type: 'website',
  jsonLd: () => [personLd.value, reviewsLd.value, breadcrumbLd.value],
})

// Robots — noindex za sve osim prve stranice (izbegavanje duplikata u indeksu)
useHead(() => ({
  meta:
    filters.page > 1
      ? [{ hid: 'robots', name: 'robots', content: 'noindex,follow' }]
      : []
}))
</script>

